/**
 * Client-side mirror of the backend `filter_expr.py` compiler - used ONLY for
 * instant inline validation and column autocomplete. It is deliberately NOT
 * trusted to produce SQL; the backend re-parses and is the sole authority. Keep
 * the grammar/limits in sync with `filter_expr.py`.
 *
 * Pure (no DOM, no atoms): callers pass a `kindOf` resolver so this stays
 * unit-testable and reusable.
 */

export const FILTER_EXPR_MAX_LEN = 1000;
const MAX_DEPTH = 32;
const MAX_NODES = 200;

/** Value kind flowing through the type check. `bool` is the only valid root. */
export type ExprKind = 'num' | 'bool' | 'str' | 'temporal';
/** Resolver: the kind of a known column/derived name, or `undefined` if unknown. */
export type KindOf = (name: string) => ExprKind | undefined;

const COMP_OPS = new Set(['==', '=', '!=', '<>', '>', '<', '>=', '<=']);
const EQUALITY_OPS = new Set(['==', '=', '!=', '<>']);
const KEYWORDS = new Set(['AND', 'OR', 'NOT']);

class ExprError extends Error {}

/* ── Tokenizer ──────────────────────────────────────────────── */

interface Tok {
  kind: string;
  val: string;
}

// Mirrors `_TOKEN_RE`: multi-char operators before single-char prefixes.
const TOKEN_RE =
  /\s+|(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?|'(?:[^']|'')*'|"(?:[^"\\\r\n]|\\.)*"|[A-Za-z_][A-Za-z0-9_]*|\[[^\]\r\n]*\]|>=|<=|==|!=|<>|[<>=+\-*/()]/y;

function tokenize(text: string): Tok[] {
  const toks: Tok[] = [];
  TOKEN_RE.lastIndex = 0;
  let pos = 0;
  while (pos < text.length) {
    TOKEN_RE.lastIndex = pos;
    const m = TOKEN_RE.exec(text);
    if (!m || m.index !== pos) {
      throw new ExprError(`Unexpected character "${text[pos]}" at position ${pos}.`);
    }
    const raw = m[0];
    pos = TOKEN_RE.lastIndex;
    if (/^\s+$/.test(raw)) continue;
    const c = raw[0]!;
    if (c >= '0' && c <= '9') {
      toks.push({ kind: 'number', val: raw });
    } else if (c === "'") {
      toks.push({ kind: 'string', val: raw.slice(1, -1).replace(/''/g, "'") });
    } else if (c === '"') {
      let name: string;
      try {
        name = JSON.parse(raw) as string;
      } catch {
        throw new ExprError(`Invalid escape in ${raw}.`);
      }
      if (!name) throw new ExprError('Empty "" column reference.');
      toks.push({ kind: 'col', val: name });
    } else if (c === '[') {
      const inner = raw.slice(1, -1).trim();
      if (!inner) throw new ExprError('Empty [] column reference.');
      toks.push({ kind: 'col', val: inner });
    } else if (/[A-Za-z_]/.test(c)) {
      const up = raw.toUpperCase();
      if (KEYWORDS.has(up)) toks.push({ kind: up, val: up });
      else toks.push({ kind: 'col', val: raw });
    } else {
      toks.push({ kind: raw, val: raw });
    }
  }
  toks.push({ kind: 'eof', val: '' });
  return toks;
}

/* ── Parser (tokens → AST) ──────────────────────────────────── */
// AST nodes mirror the backend tuples, as arrays tagged by element 0.
type Node =
  | ['num', string]
  | ['str', string]
  | ['col', string]
  | ['unary', string, Node]
  | ['arith', string, Node, Node]
  | ['cmp', string, Node, Node]
  | ['bool', string, Node, Node]
  | ['not', Node];

class Parser {
  private i = 0;
  private depth = 0;
  private nodes = 0;
  constructor(private toks: Tok[]) {}

  private peek(): Tok {
    return this.toks[this.i]!;
  }
  private advance(): Tok {
    return this.toks[this.i++]!;
  }
  private node<T extends Node>(n: T): T {
    if (++this.nodes > MAX_NODES) throw new ExprError('Expression is too complex.');
    return n;
  }
  private descend(): void {
    if (++this.depth > MAX_DEPTH) throw new ExprError('Expression is nested too deeply.');
  }

  parse(): Node {
    const n = this.parseOr();
    if (this.peek().kind !== 'eof') {
      throw new ExprError(
        `Unexpected "${this.peek().val}" - check operators and parentheses.`,
      );
    }
    return n;
  }

  private parseOr(): Node {
    this.descend();
    let n = this.parseAnd();
    while (this.peek().kind === 'OR') {
      this.advance();
      n = this.node(['bool', 'OR', n, this.parseAnd()]);
    }
    this.depth--;
    return n;
  }
  private parseAnd(): Node {
    let n = this.parseNot();
    while (this.peek().kind === 'AND') {
      this.advance();
      n = this.node(['bool', 'AND', n, this.parseNot()]);
    }
    return n;
  }
  private parseNot(): Node {
    if (this.peek().kind === 'NOT') {
      this.descend();
      this.advance();
      const n = this.node(['not', this.parseNot()] as Node);
      this.depth--;
      return n;
    }
    return this.parseCmp();
  }
  private parseCmp(): Node {
    const left = this.parseAdd();
    if (COMP_OPS.has(this.peek().kind)) {
      const op = this.advance().kind;
      return this.node(['cmp', op, left, this.parseAdd()]);
    }
    return left;
  }
  private parseAdd(): Node {
    let n = this.parseMul();
    while (this.peek().kind === '+' || this.peek().kind === '-') {
      const op = this.advance().kind;
      n = this.node(['arith', op, n, this.parseMul()]);
    }
    return n;
  }
  private parseMul(): Node {
    let n = this.parseUnary();
    while (this.peek().kind === '*' || this.peek().kind === '/') {
      const op = this.advance().kind;
      n = this.node(['arith', op, n, this.parseUnary()]);
    }
    return n;
  }
  private parseUnary(): Node {
    if (this.peek().kind === '+' || this.peek().kind === '-') {
      this.descend();
      const op = this.advance().kind;
      const n = this.node(['unary', op, this.parseUnary()]);
      this.depth--;
      return n;
    }
    return this.parsePrimary();
  }
  private parsePrimary(): Node {
    const t = this.peek();
    if (t.kind === 'number') {
      this.advance();
      return this.node(['num', t.val]);
    }
    if (t.kind === 'string') {
      this.advance();
      return this.node(['str', t.val]);
    }
    if (t.kind === 'col') {
      this.advance();
      return this.node(['col', t.val]);
    }
    if (t.kind === '(') {
      this.descend();
      this.advance();
      const n = this.parseOr();
      if (this.peek().kind !== ')') throw new ExprError("Unbalanced parentheses - missing ')'.");
      this.advance();
      this.depth--;
      return n;
    }
    if (t.kind === 'eof') throw new ExprError('Unexpected end of expression.');
    throw new ExprError(`Expected a value but found "${t.val}".`);
  }
}

/* ── Type check (mirror of the backend emitter's kinds) ─────── */

function checkKind(node: Node, kindOf: KindOf): ExprKind {
  switch (node[0]) {
    case 'num':
      return 'num';
    case 'str':
      return 'str';
    case 'col': {
      const k = kindOf(node[1]);
      if (k === undefined) throw new ExprError(`Unknown column "${node[1]}".`);
      return k;
    }
    case 'unary': {
      if (checkKind(node[2], kindOf) !== 'num') throw new ExprError('Unary sign needs a number.');
      return 'num';
    }
    case 'arith': {
      if (checkKind(node[2], kindOf) !== 'num' || checkKind(node[3], kindOf) !== 'num') {
        throw new ExprError(`"${node[1]}" needs numbers.`);
      }
      return 'num';
    }
    case 'cmp': {
      const l = checkKind(node[2], kindOf);
      const r = checkKind(node[3], kindOf);
      if (l === 'num' && r === 'num') {
        // ok
      } else if ((l === 'temporal' || l === 'str') && (r === 'temporal' || r === 'str') && (l === 'temporal' || r === 'temporal')) {
        // temporal vs temporal/string-literal
      } else if (l === 'str' && r === 'str') {
        if (!EQUALITY_OPS.has(node[1])) {
          throw new ExprError(`Operator "${node[1]}" is not valid for text - use == or !=.`);
        }
      } else {
        throw new ExprError(`Cannot compare ${l} and ${r} - types must match.`);
      }
      return 'bool';
    }
    case 'bool': {
      if (checkKind(node[2], kindOf) !== 'bool' || checkKind(node[3], kindOf) !== 'bool') {
        throw new ExprError(`"${node[1]}" needs conditions on both sides.`);
      }
      return 'bool';
    }
    case 'not': {
      if (checkKind(node[1], kindOf) !== 'bool') throw new ExprError('NOT needs a condition.');
      return 'bool';
    }
  }
}

/**
 * Validate an expression. Returns `null` when valid (or blank), else a
 * user-facing error message. `kindOf` resolves a name to its kind (or
 * `undefined` if it isn't a known column/derived name).
 */
export function validateExpr(text: string, kindOf: KindOf): string | null {
  const t = (text ?? '').trim();
  if (!t) return null;
  if (t.length > FILTER_EXPR_MAX_LEN) {
    return `Expression too long (max ${FILTER_EXPR_MAX_LEN} characters).`;
  }
  try {
    const ast = new Parser(tokenize(t)).parse();
    if (checkKind(ast, kindOf) !== 'bool') {
      return "Expression must be a condition, e.g. 'rpm > stw * 2'.";
    }
    return null;
  } catch (e) {
    return e instanceof ExprError ? e.message : 'Invalid expression.';
  }
}

/**
 * Validate a derived-column expression: it must compute a NUMBER (arithmetic
 * over numeric columns/constants). Comparisons, booleans, and text are rejected.
 * Mirrors the backend `compile_value_expr`. Returns `null` when valid/blank.
 */
export function validateValueExpr(text: string, kindOf: KindOf): string | null {
  const t = (text ?? '').trim();
  if (!t) return null;
  if (t.length > FILTER_EXPR_MAX_LEN) {
    return `Expression too long (max ${FILTER_EXPR_MAX_LEN} characters).`;
  }
  try {
    const ast = new Parser(tokenize(t)).parse();
    if (checkKind(ast, kindOf) !== 'num') {
      return "Expression must compute a number, e.g. 'rpm * 2 + stw'.";
    }
    return null;
  } catch (e) {
    return e instanceof ExprError ? e.message : 'Invalid expression.';
  }
}

/* ── Autocomplete: the column token under the caret ─────────── */

export interface CaretToken {
  /** The fragment being typed (text the suggestion should match/replace). */
  fragment: string;
  /** Replace [start, end) in the source with the chosen, wrapped column. */
  start: number;
  end: number;
  /** True when the fragment sits inside an unclosed `[` bracket. */
  bracket: boolean;
}

/**
 * The column-name fragment under the caret, or `null` when the caret is not in
 * an operand position where a column would be valid (so suggestions don't fight
 * normal typing). Pure - used by the expression box for autocomplete.
 */
export function caretToken(text: string, caret: number): CaretToken | null {
  const before = text.slice(0, caret);

  // Inside an unclosed bracket? Find the last '[' with no ']' after it.
  const lb = before.lastIndexOf('[');
  if (lb !== -1 && before.indexOf(']', lb) === -1) {
    return { fragment: before.slice(lb + 1), start: lb, end: caret, bracket: true };
  }

  // Otherwise a bare identifier run ending at the caret.
  const m = /[A-Za-z_][A-Za-z0-9_]*$/.exec(before);
  const fragment = m ? m[0] : '';
  const start = m ? caret - fragment.length : caret;
  // A keyword being typed isn't a column.
  if (fragment && KEYWORDS.has(fragment.toUpperCase())) return null;
  if (!operandPosition(before.slice(0, start))) return null;
  return { fragment, start, end: caret, bracket: false };
}

/** True when `prefix` ends where an operand (column/number) may begin. */
function operandPosition(prefix: string): boolean {
  const p = prefix.replace(/\s+$/, '');
  if (!p) return true;
  const last = p[p.length - 1]!;
  if ('([+-*/<>=!,'.includes(last)) return true;
  if (last === ')' || last === ']' || last === "'") return false;
  // After an AND/OR/NOT keyword (word boundary) we're at an operand.
  return /(?:^|[^A-Za-z0-9_])(AND|OR|NOT)$/i.test(p);
}

/**
 * Wrap a column name for insertion: bare when it's a simple identifier,
 * `[bracketed]` otherwise (matches the grammar). Mirrors the bare-IDENT charset.
 */
export function wrapColumnForExpr(name: string): string {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) ? name : `[${name}]`;
}
